import { wccModules } from "enums/wccModules";
import { inject, injectable } from "inversify";
import { ObservableArray, PureComputed, Subscribable, SubscribableOrValue } from "knockout";
import { EffectsContainer } from "../../../mixins/withEffects";
import DropdownVM, { Dropdown, DropdownItem, DropdownItemOrPredicate, JSONDropdown } from "../base/base";
import './multiselect.html';

const itemMaxWidth = 35;

interface JSONMultiSelectDropdown<T = any> extends JSONDropdown<T> {
    defaultTitle?: SubscribableOrValue<string | undefined>
    showReset?: SubscribableOrValue<boolean | undefined>
}

export class MultiSelectDropdown<T = any> extends Dropdown<T> {
    selectedItems: ObservableArray<DropdownItem<T>>
    title: PureComputed<string>

    defaultTitle: PureComputed<string>
    showReset: PureComputed<boolean>

    constructor(jsonModel?: JSONMultiSelectDropdown<T>) {
        super(jsonModel);

        this.selectedItems = ko.observableArray().extend({ rateLimit: 0 });

        this.defaultTitle = ko.observable(jsonModel?.defaultTitle).unwrap().default('None');
        this.showReset = ko.observable(jsonModel?.showReset).unwrap().default(false);

        this.title = ko.pureComputed(() => this.getTitle());
    }

    toggle(itemOrPredicate: DropdownItemOrPredicate<T>) {
        var item = this.findItem(itemOrPredicate);

        if (item) {
            if (!this.selectedItems().includes(item))
                this.selectedItems.push(item);
            else
                this.selectedItems.remove(item);
        }
    }

    select(itemOrPredicate: DropdownItemOrPredicate<T>) {
        var item = this.findItem(itemOrPredicate);

        if (item && !this.selectedItems().includes(item))
            this.selectedItems.push(item);
    }

    deselect(itemOrPredicate: DropdownItemOrPredicate<T>) {
        var item = this.findItem(itemOrPredicate);

        if (item)
            this.selectedItems.remove(item);
    }

    clear() {
        this.selectedItems([]);
    }

    private getTitle() {
        if (this.selectedItems().length === 0)
            return this.defaultTitle();

        return this.selectedItems().reduce((result, item, idx, list) => {
            if (idx > 0) {
                if (idx === list.length - 1)
                    result += ' and ';
                else
                    result += ', ';
            }

            var title = ko.unwrap(item.title) || '';

            if (title.length > itemMaxWidth)
                title = title.substr(0, itemMaxWidth - 3) + '...';

            result += title;

            return result;
        }, '');
    }
}

@injectable()
export default class MultiSelectDropdownVM extends DropdownVM {
    labels = labels

    headerNodes: JQuery<any>
    itemNodes: JQuery<any>

    defaultTitle: PureComputed<string>
    selection: PureComputed<string>

    items: Subscribable<Array<DropdownItem>>
    selectedItems: ObservableArray<DropdownItem>

    showReset: PureComputed<boolean>
    noneSelected: Subscribable<boolean>

    dropdown: MultiSelectDropdown

    toggle = (itemOrPredicate: DropdownItemOrPredicate) => this.dropdown.toggle(itemOrPredicate)
    clear = () => this.dropdown.clear()

    constructor(
        @inject(wccModules.componentConfig) model: MultiSelectDropdown,
        @inject(wccModules.componentInfo) info: ko.components.ComponentInfo,
        @inject(wccModules.effects) effects: EffectsContainer
    ) {
        super(model, info);

        this.dropdown = model;

        this.headerNodes = $(info.templateNodes).filter('select-head').contents();
        this.itemNodes = $(info.templateNodes).filter('select-item').contents();

        if (!_(this.headerNodes).any())
            this.headerNodes = $('<span class="wcc-link" data-bind="text: selection"></span>');

        if (!_(this.itemNodes).any())
            this.itemNodes = $('<span data-bind="text: title"></span>');

        this.defaultTitle = model.defaultTitle;        
        this.selection = model.title;

        this.items = model.items;
        this.selectedItems = model.selectedItems;

        this.showReset = model.showReset;
        this.noneSelected = model.selectedItems.isEmpty();

        effects.register((items, selectedItems) => {
            selectedItems.forEach(selectedItem => {
                if (!items.includes(selectedItem))
                    this.selectedItems.remove(selectedItem);
            });
        }, [this.items, this.selectedItems]);
    }
}